import React, { useState, useEffect, useCallback } from 'react';
import { cn, isMobile, formatNumber, formatRelativeTime } from '../lib/utils';
import DashboardMap from './DashboardMap';
import { 
  AlertTriangle, 
  Users, 
  Package, 
  Zap, 
  CheckCircle,
  Target,
  Clock,
  Calendar,
  Activity,
  MessageSquare,
  Radio,
  Phone,
  FileText,
  Download,
  Eye,
  Flame,
  Droplets,
  Wind,
  Heart,
  Truck
} from 'lucide-react';

interface SituationAnalysisProps {}

type UserRole = 'field_coordinator' | 'sector_specialist' | 'emergency_manager' | 'info_manager';
type ViewLevel = 'primary' | 'secondary' | 'tertiary';
type SectionVisibility = {
  timeline: boolean;
  decisions: boolean;
  resources: boolean;
  communications: boolean;
  documents: boolean;
};

interface MockCrisis {
  id: number;
  title: string;
  type: string;
  status: string;
  severity: number;
  location: string;
  coordinates?: [number, number];
  affected_population: number;
  created_at: string;
  lastUpdate: string;
  response_agencies?: string[];
  description?: string;
}

interface MockStats {
  activeCrises?: number;
  affectedPopulation?: number;
  responsesActive?: number;
  alertsToday?: number;
  totalCrises?: number;
  totalDecisions?: number;
  averageConfidence?: number;
  resourcesDeployed?: number;
  pendingDecisions?: number;
}

interface Alert {
  event_id: string;
  detection_time: string;
  status: string;
  confidence: number;
  severity: number;
  crisis_type: string;
  location: string;
  priority: 'high' | 'medium' | 'low';
}

interface TimelineEvent {
  id: string;
  timestamp: string;
  type: 'incident' | 'response' | 'decision' | 'update' | 'alert';
  title: string;
  description: string;
  severity?: number;
  actor?: string;
}

interface QuickAction {
  id: string;
  label: string;
  icon: string;
  type: 'decision' | 'alert' | 'response' | 'communication';
  priority: 'high' | 'medium' | 'low';
  enabled: boolean;
}

interface Communication {
  id: string;
  timestamp: string;
  type: 'sitrep' | 'coordination' | 'public' | 'internal';
  title: string;
  sender: string;
  priority: 'high' | 'medium' | 'low';
  status: 'sent' | 'draft' | 'pending';
}

interface Document {
  id: string;
  title: string;
  type: 'assessment' | 'plan' | 'report' | 'briefing';
  lastModified: string;
  author: string;
  status: 'final' | 'draft' | 'review';
  size: string;
}

interface Resource {
  id: string;
  name: string;
  type: 'personnel' | 'equipment' | 'supplies';
  total: number;
  deployed: number;
  available: number;
  status: 'adequate' | 'low' | 'critical';
  location?: string;
}

interface Decision {
  id: string;
  title: string;
  type: 'immediate' | 'strategic' | 'tactical';
  status: 'active' | 'pending' | 'completed';
  priority: 'high' | 'medium' | 'low';
  confidence: number;
  timestamp: string;
  assignee?: string;
  deadline?: string;
}

// Utility functions
const getSeverityColorClass = (severity: number): string => {
  if (severity >= 5) return 'bg-accent-100 text-accent-700 border-accent-300';
  if (severity >= 4) return 'bg-accent-100 text-accent-700 border-accent-300';
  if (severity >= 3) return 'bg-ocean-100 text-ocean-700 border-ocean-300';
  if (severity >= 2) return 'bg-ink-100 text-ink-700 border-ink-300';
  return 'bg-ink-100 text-ink-700 border-ink-300';
};

const getSeverityText = (severity: number): string => {
  const levels: Record<number, string> = {
    1: 'Low', 2: 'Minor', 3: 'Medium', 4: 'High', 5: 'Critical'
  };
  return levels[severity] || 'Unknown';
};

const getStatusColor = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'active': return 'bg-accent-100 text-accent-700 border-accent-300';
    case 'emerging': return 'bg-accent-100 text-accent-700 border-accent-300';
    case 'stabilizing': return 'bg-ocean-100 text-ocean-700 border-ocean-300';
    case 'resolved': return 'bg-sage-100 text-sage-700 border-sage-300';
    case 'pending': return 'bg-sand-100 text-sand-700 border-sand-300';
    default: return 'bg-ink-100 text-ink-700 border-ink-300';
  }
};

const getPriorityColor = (priority: string): string => {
  switch (priority?.toLowerCase()) {
    case 'high': return 'bg-accent-100 text-accent-700 border-accent-300';
    case 'medium': return 'bg-ocean-100 text-ocean-700 border-ocean-300';
    case 'low': return 'bg-sage-100 text-sage-700 border-sage-300';
    default: return 'bg-ink-100 text-ink-700 border-ink-300';
  }
};

const SituationAnalysis: React.FC<SituationAnalysisProps> = () => {
  const [currentRole, setCurrentRole] = useState<UserRole>('field_coordinator');
  const [crises, setCrises] = useState<MockCrisis[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [stats, setStats] = useState<MockStats>({});
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isMobileView, setIsMobileView] = useState(isMobile());
  const [selectedCrisis, setSelectedCrisis] = useState<MockCrisis | null>(null);
  const [expandedSections, setExpandedSections] = useState<{
    timeline: boolean;
    decisions: boolean;
    resources: boolean;
    communications: boolean;
    documents: boolean;
  }>({
    timeline: false,
    decisions: false,
    resources: false,
    communications: false,
    documents: false
  });

  // Role configuration - determines view level and visible sections
  const roleConfig = {
    field_coordinator: { 
      viewLevel: 'primary' as ViewLevel, 
      name: 'Field Coordinator',
      description: 'Essential metrics and quick decisions',
      sections: {
        timeline: true,
        decisions: true,
        resources: false,
        communications: true,
        documents: false
      }
    },
    sector_specialist: { 
      viewLevel: 'secondary' as ViewLevel, 
      name: 'Sector Specialist',
      description: 'Detailed analytics and sector-specific data',
      sections: {
        timeline: true,
        decisions: true,
        resources: true,
        communications: true,
        documents: true
      }
    },
    emergency_manager: { 
      viewLevel: 'secondary' as ViewLevel, 
      name: 'Emergency Manager',
      description: 'Strategic oversight and resource coordination',
      sections: {
        timeline: true,
        decisions: true,
        resources: true,
        communications: true,
        documents: true
      }
    },
    info_manager: { 
      viewLevel: 'tertiary' as ViewLevel, 
      name: 'Information Manager',
      description: 'System administration and data management',
      sections: {
        timeline: true,
        decisions: true,
        resources: true,
        communications: true,
        documents: true
      }
    }
  };

  const currentConfig = roleConfig[currentRole];

  // Toggle section expansion
  const toggleSection = (section: keyof SectionVisibility) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Quick Actions based on role and current context
  const getQuickActions = useCallback((): QuickAction[] => {
    const baseActions = [
      {
        id: 'new-decision',
        label: 'Make Decision',
        icon: '⚡',
        type: 'decision' as const,
        priority: 'high' as const,
        enabled: selectedCrisis !== null
      },
      {
        id: 'triage-alerts',
        label: 'Triage Alerts',
        icon: '🚨',
        type: 'alert' as const,
        priority: 'high' as const,
        enabled: alerts.some(a => a.status === 'Active')
      },
      {
        id: 'deploy-resources',
        label: 'Deploy Resources',
        icon: '📦',
        type: 'response' as const,
        priority: 'medium' as const,
        enabled: stats.activeCrises && stats.activeCrises > 0
      }
    ];

    if (currentConfig.viewLevel === 'secondary' || currentConfig.viewLevel === 'tertiary') {
      baseActions.push({
        id: 'coordination-call',
        label: 'Coordinate Response',
        icon: '📞',
        type: 'communication' as const,
        priority: 'medium' as const,
        enabled: true
      });
    }

    return baseActions;
  }, [selectedCrisis, alerts, stats, currentConfig.viewLevel]);

  // Handle window resize and online status
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile());
    };
    
    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
      if (navigator.onLine) {
        setLastUpdated(new Date());
        fetchData(); // Refresh data when coming back online
      }
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds when online
    const interval = setInterval(() => {
      if (navigator.onLine) {
        setLastUpdated(new Date());
        fetchData();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Mock data - in real implementation would fetch from API
      const mockCrises: MockCrisis[] = [
        {
          id: 1,
          title: "Syria Conflict - Aleppo Region",
          type: "conflict",
          status: "active",
          severity: 5,
          location: "Aleppo, Syria",
          coordinates: [36.2021, 37.1343],
          affected_population: 245000,
          created_at: new Date(Date.now() - 86400000 * 3).toISOString(),
          lastUpdate: new Date(Date.now() - 1800000).toISOString(),
          response_agencies: ['UNHCR', 'WFP', 'UNICEF'],
          description: "Escalating conflict affecting civilian populations in urban areas"
        },
        {
          id: 2,
          title: "Bangladesh Monsoon Floods",
          type: "flood",
          status: "active", 
          severity: 4,
          location: "Sylhet Division, Bangladesh",
          coordinates: [24.8949, 91.8687],
          affected_population: 180000,
          created_at: new Date(Date.now() - 86400000 * 2).toISOString(),
          lastUpdate: new Date(Date.now() - 3600000).toISOString(),
          response_agencies: ['UN OCHA', 'Red Cross'],
          description: "Monsoon flooding displacing communities and affecting infrastructure"
        },
        {
          id: 3,
          title: "Somalia Drought Emergency",
          type: "drought",
          status: "active",
          severity: 5,
          location: "Bay Region, Somalia",
          coordinates: [2.0469, 44.0154],
          affected_population: 320000,
          created_at: new Date(Date.now() - 86400000 * 14).toISOString(),
          lastUpdate: new Date(Date.now() - 10800000).toISOString(),
          response_agencies: ['WFP', 'UNICEF', 'WHO'],
          description: "Severe drought conditions leading to food insecurity and displacement"
        }
      ];

      const mockAlerts: Alert[] = [
        {
          event_id: 'EID-2024-001',
          detection_time: new Date(Date.now() - 3600000).toISOString(),
          status: 'Active',
          confidence: 85,
          severity: 70,
          crisis_type: 'Flood',
          location: 'Northern Bangladesh',
          priority: 'high'
        },
        {
          event_id: 'EID-2024-002',
          detection_time: new Date(Date.now() - 7200000).toISOString(),
          status: 'Active',
          confidence: 92,
          severity: 88,
          crisis_type: 'Conflict',
          location: 'Eastern Syria',
          priority: 'high'
        },
        {
          event_id: 'EID-2024-003',
          detection_time: new Date(Date.now() - 1800000).toISOString(),
          status: 'Pending',
          confidence: 68,
          severity: 45,
          crisis_type: 'Disease Outbreak',
          location: 'Central Somalia',
          priority: 'medium'
        }
      ];

      const mockStats: MockStats = {
        activeCrises: 3,
        affectedPopulation: 745000,
        responsesActive: 8,
        alertsToday: 12,
        totalCrises: 3,
        totalDecisions: 47,
        averageConfidence: 82,
        resourcesDeployed: 15,
        pendingDecisions: 4
      };

      const mockTimeline: TimelineEvent[] = [
        {
          id: '1',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          type: 'alert',
          title: 'New Crisis Alert',
          description: 'Flood detected in Northern Bangladesh - EID-2024-001',
          severity: 4,
          actor: 'Crisis Detection Engine'
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          type: 'decision',
          title: 'Emergency Response Decision',
          description: 'Approved deployment of emergency shelter supplies to Syria',
          actor: 'Field Coordinator - Sarah Mitchell'
        },
        {
          id: '3',
          timestamp: new Date(Date.now() - 10800000).toISOString(),
          type: 'response',
          title: 'Resource Deployment',
          description: 'Mobile health unit dispatched to Somalia drought zone',
          actor: 'WHO Emergency Team'
        },
        {
          id: '4',
          timestamp: new Date(Date.now() - 14400000).toISOString(),
          type: 'update',
          title: 'Assessment Update',
          description: 'Revised affected population estimates for Bangladesh floods',
          actor: 'Field Assessment Team'
        }
      ];

      const mockCommunications: Communication[] = [
        {
          id: 'comm-001',
          timestamp: new Date(Date.now() - 1800000).toISOString(),
          type: 'sitrep',
          title: 'Daily Situation Report #45',
          sender: 'Regional Information Officer',
          priority: 'high',
          status: 'sent'
        },
        {
          id: 'comm-002',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          type: 'coordination',
          title: 'Inter-agency coordination call',
          sender: 'Cluster Lead',
          priority: 'medium',
          status: 'sent'
        },
        {
          id: 'comm-003',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          type: 'internal',
          title: 'Resource allocation update',
          sender: 'Operations Manager',
          priority: 'medium',
          status: 'draft'
        }
      ];

      const mockDocuments: Document[] = [
        {
          id: 'doc-001',
          title: 'Rapid Assessment Report - Bangladesh Floods',
          type: 'assessment',
          lastModified: new Date(Date.now() - 1800000).toISOString(),
          author: 'Assessment Team Lead',
          status: 'final',
          size: '2.3 MB'
        },
        {
          id: 'doc-002',
          title: 'Emergency Response Plan Update',
          type: 'plan',
          lastModified: new Date(Date.now() - 3600000).toISOString(),
          author: 'Emergency Manager',
          status: 'review',
          size: '1.8 MB'
        },
        {
          id: 'doc-003',
          title: 'Executive Briefing - Regional Overview',
          type: 'briefing',
          lastModified: new Date(Date.now() - 7200000).toISOString(),
          author: 'Information Manager',
          status: 'draft',
          size: '850 KB'
        }
      ];

      const mockResources: Resource[] = [
        {
          id: 'res-001',
          name: 'Emergency Response Teams',
          type: 'personnel',
          total: 45,
          deployed: 32,
          available: 13,
          status: 'adequate',
          location: 'Regional Hub'
        },
        {
          id: 'res-002',
          name: 'Emergency Shelter Kits',
          type: 'supplies',
          total: 500,
          deployed: 385,
          available: 115,
          status: 'low'
        },
        {
          id: 'res-003',
          name: 'Water Purification Units',
          type: 'equipment',
          total: 12,
          deployed: 10,
          available: 2,
          status: 'critical'
        }
      ];

      const mockDecisions: Decision[] = [
        {
          id: 'dec-001',
          title: 'Deploy additional medical supplies',
          type: 'immediate',
          status: 'active',
          priority: 'high',
          confidence: 92,
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          assignee: 'Logistics Coordinator',
          deadline: new Date(Date.now() + 86400000).toISOString()
        },
        {
          id: 'dec-002',
          title: 'Establish temporary coordination hub',
          type: 'tactical',
          status: 'pending',
          priority: 'medium',
          confidence: 78,
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          assignee: 'Field Coordinator'
        },
        {
          id: 'dec-003',
          title: 'Scale up nutrition programming',
          type: 'strategic',
          status: 'completed',
          priority: 'high',
          confidence: 95,
          timestamp: new Date(Date.now() - 172800000).toISOString()
        }
      ];

      setCrises(mockCrises);
      setAlerts(mockAlerts);
      setStats(mockStats);
      setTimeline(mockTimeline);
      setCommunications(mockCommunications);
      setDocuments(mockDocuments);
      setResources(mockResources);
      setDecisions(mockDecisions);
      
      // Auto-select first crisis for primary view
      if (currentConfig.viewLevel === 'primary' && mockCrises.length > 0) {
        setSelectedCrisis(mockCrises[0]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching situation analysis data:', error);
      setLoading(false);
    }
  };

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 80) return 'bg-sage-500';
    if (confidence >= 60) return 'bg-ocean-500';
    return 'bg-accent-500';
  };

  const visibleSections = currentConfig.sections;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
          <p className="mt-4 text-ink-600">Loading situation analysis...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Role Selector and Status */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-6 border-b-2 border-ink-200">
        <div>
          <h1 className="text-3xl font-display text-ink-900 mb-2">Situation Analysis</h1>
          <p className="text-ink-600">{currentConfig.description}</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Role Selector */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-mono uppercase tracking-ultra text-ink-500">Role</label>
            <select 
              value={currentRole} 
              onChange={(e) => setCurrentRole(e.target.value as UserRole)}
              className="input-minimal py-2 text-sm min-w-[180px]"
            >
              {Object.entries(roleConfig).map(([role, config]) => (
                <option key={role} value={role}>{config.name}</option>
              ))}
            </select>
          </div>
          
          {/* Status Indicator */}
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-2 h-2 rounded-full",
              isOnline ? "bg-sage-500" : "bg-accent-500"
            )}></div>
            <span className="text-xs font-mono text-ink-500">
              {isOnline ? 'ONLINE' : 'OFFLINE'} • Updated {formatRelativeTime(lastUpdated)}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      {!isMobileView && (
        <div className="flex flex-wrap gap-3 py-4 border-b border-ink-200">
          {getQuickActions().map((action) => (
            <button
              key={action.id}
              disabled={!action.enabled}
              className={cn(
                'btn btn-ghost flex items-center gap-2 px-4 py-2 text-sm',
                action.priority === 'high' && action.enabled && 'border-accent-300 text-accent-600 hover:bg-accent-50',
                !action.enabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span>{action.icon}</span>
              {action.label}
            </button>
          ))}
        </div>
      )}


      {/* Crisis Overview Section */}
      <OverviewSection 
        stats={stats} 
        crises={crises}
        alerts={alerts}
        selectedCrisis={selectedCrisis}
        setSelectedCrisis={setSelectedCrisis}
        viewLevel={currentConfig.viewLevel}
        isMobileView={isMobileView}
      />

      {/* Unified Dashboard Grid */}
      <div className={cn(
        "grid gap-6",
        isMobileView 
          ? "grid-cols-1" 
          : currentConfig.viewLevel === 'primary'
            ? "grid-cols-1 lg:grid-cols-2"
            : "grid-cols-1 lg:grid-cols-12"
      )}>
        {/* Left Column - Timeline and Communications */}
        <div className={cn(
          "space-y-6",
          !isMobileView && currentConfig.viewLevel !== 'primary' && "lg:col-span-4"
        )}>
          {visibleSections.timeline && (
            <TimelineSection 
              timeline={timeline}
              expanded={expandedSections.timeline}
              onToggle={() => toggleSection('timeline')}
              viewLevel={currentConfig.viewLevel}
              isMobileView={isMobileView}
            />
          )}
          
          {visibleSections.communications && (
            <CommunicationsSection 
              communications={communications}
              expanded={expandedSections.communications}
              onToggle={() => toggleSection('communications')}
              viewLevel={currentConfig.viewLevel}
              isMobileView={isMobileView}
            />
          )}
        </div>

        {/* Center Column - Decisions and Critical Actions */}
        <div className={cn(
          "space-y-6",
          !isMobileView && currentConfig.viewLevel !== 'primary' && "lg:col-span-4"
        )}>
          {visibleSections.decisions && (
            <DecisionsSection 
              decisions={decisions}
              selectedCrisis={selectedCrisis}
              expanded={expandedSections.decisions}
              onToggle={() => toggleSection('decisions')}
              viewLevel={currentConfig.viewLevel}
              isMobileView={isMobileView}
            />
          )}
        </div>

        {/* Right Column - Resources and Documents */}
        {!isMobileView && currentConfig.viewLevel !== 'primary' && (
          <div className="lg:col-span-4 space-y-6">
            {visibleSections.resources && (
              <ResourcesSection 
                resources={resources}
                expanded={expandedSections.resources}
                onToggle={() => toggleSection('resources')}
                viewLevel={currentConfig.viewLevel}
                isMobileView={isMobileView}
              />
            )}
            
            {visibleSections.documents && (
              <DocumentsSection 
                documents={documents}
                expanded={expandedSections.documents}
                onToggle={() => toggleSection('documents')}
                viewLevel={currentConfig.viewLevel}
                isMobileView={isMobileView}
              />
            )}
          </div>
        )}

        {/* Mobile: Stack Resources and Documents below */}
        {isMobileView && (
          <>
            {visibleSections.resources && (
              <ResourcesSection 
                resources={resources}
                expanded={expandedSections.resources}
                onToggle={() => toggleSection('resources')}
                viewLevel={currentConfig.viewLevel}
                isMobileView={isMobileView}
              />
            )}
            
            {visibleSections.documents && (
              <DocumentsSection 
                documents={documents}
                expanded={expandedSections.documents}
                onToggle={() => toggleSection('documents')}
                viewLevel={currentConfig.viewLevel}
                isMobileView={isMobileView}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Overview Section Component - Crisis overview and key metrics
const OverviewSection: React.FC<{
  stats: MockStats;
  crises: MockCrisis[];
  alerts: Alert[];
  selectedCrisis: MockCrisis | null;
  setSelectedCrisis: (crisis: MockCrisis | null) => void;
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ stats, crises, alerts, selectedCrisis, setSelectedCrisis, viewLevel, isMobileView }) => {
  
  const keyMetrics = [
    { label: 'Active Crises', value: stats.activeCrises || 0, trend: '+2', icon: AlertTriangle },
    { label: 'Affected Population', value: formatNumber(stats.affectedPopulation || 0), trend: '+12K', icon: Users },
    { label: 'Active Responses', value: stats.responsesActive || 0, trend: '+3', icon: Package },
    { label: 'Alerts Today', value: stats.alertsToday || 0, trend: '+5', icon: Zap }
  ];

  if (viewLevel === 'secondary' || viewLevel === 'tertiary') {
    keyMetrics.push(
      { label: 'Total Decisions', value: stats.totalDecisions || 0, trend: '+8', icon: CheckCircle },
      { label: 'Avg Confidence', value: `${stats.averageConfidence || 0}%`, trend: '+2%', icon: Target }
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <div className={cn(
        "grid gap-4",
        isMobileView ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4 lg:grid-cols-6"
      )}>
        {keyMetrics.map((metric) => (
          <div key={metric.label} className="card-minimal p-4">
            <div className="flex items-center justify-between mb-2">
              <metric.icon className="h-6 w-6 text-ink-600" />
              <span className="text-xs font-mono text-sage-600">{metric.trend}</span>
            </div>
            <div className="text-2xl font-bold text-ink-900 mb-1">{metric.value}</div>
            <div className="text-xs font-mono uppercase tracking-wider text-ink-500">{metric.label}</div>
          </div>
        ))}
      </div>

      {/* Satellite Map Section */}
      <div className="card-minimal p-0 overflow-hidden mb-6">
        <div className="p-6 pb-4 border-b border-ink-200">
          <h3 className="text-lg font-display text-ink-900 mb-2 flex items-center gap-2">
            <Flame className="h-5 w-5 text-red-500" />
            Crisis Locations - Satellite View
          </h3>
          <p className="text-sm text-ink-600">
            Live satellite imagery showing active crisis locations and affected regions
          </p>
        </div>
        <div className="h-[500px]">
          <DashboardMap 
            height="100%" 
            showControls={true}
            onCrisisSelect={setSelectedCrisis}
            singleCrisis={false}
            defaultSatellite={true}
          />
        </div>
      </div>

      {/* Active Alerts & Crisis Summary */}
      <div className={cn(
        "grid gap-6",
        isMobileView ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-3"
      )}>
        {/* Active Crises List */}
        <div className="card-minimal p-6">
          <h3 className="text-lg font-display text-ink-900 mb-4 flex items-center justify-between">
            Active Crises
            <span className="text-xs font-mono text-ink-500 bg-ink-100 px-2 py-1">{crises.length}</span>
          </h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {crises.map((crisis) => (
              <div 
                key={crisis.id}
                onClick={() => setSelectedCrisis(crisis)}
                className={cn(
                  "p-3 border-2 cursor-pointer transition-all text-sm",
                  selectedCrisis?.id === crisis.id 
                    ? "border-accent-300 bg-accent-50" 
                    : "border-ink-200 hover:border-accent-200"
                )}
              >
                <div className="flex items-start justify-between mb-1">
                  <h4 className="font-medium text-ink-900 text-sm leading-tight">{crisis.title}</h4>
                  <span className={cn(
                    "text-xs px-1 py-0.5 border text-center min-w-[3rem]",
                    getSeverityColorClass(crisis.severity)
                  )}>
                    {crisis.severity}
                  </span>
                </div>
                <div className="text-xs text-ink-600 mb-1">{crisis.location}</div>
                <div className="text-xs text-ink-500">
                  {formatNumber(crisis.affected_population)} affected • {formatRelativeTime(new Date(crisis.lastUpdate))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Alerts */}
        <div className="card-minimal p-6">
          <h3 className="text-lg font-display text-ink-900 mb-4 flex items-center justify-between">
            Recent Alerts
            <span className="text-xs font-mono text-ink-500 bg-ink-100 px-2 py-1">{alerts.length}</span>
          </h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {alerts.slice(0, 4).map((alert) => (
              <div key={alert.event_id} className="p-3 border border-ink-200 text-sm">
                <div className="flex items-start justify-between mb-1">
                  <span className="font-mono text-xs">{alert.event_id}</span>
                  <div className="flex items-center gap-1">
                    <div className={cn("w-2 h-2", getPriorityColor(alert.priority))} />
                    <span className="text-xs font-medium">{alert.confidence}%</span>
                  </div>
                </div>
                <div className="font-medium text-ink-900 mb-1">{alert.crisis_type}</div>
                <div className="text-xs text-ink-600">{alert.location}</div>
                <div className="text-xs text-ink-500 mt-1">
                  {formatRelativeTime(new Date(alert.detection_time))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Crisis Detail */}
        <div className="card-minimal p-6">
          {selectedCrisis ? (
            <div>
              <h3 className="text-lg font-display text-ink-900 mb-4">Crisis Focus</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-ink-900 mb-1">{selectedCrisis.title}</h4>
                  <p className="text-sm text-ink-600">{selectedCrisis.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider text-ink-500 mb-1">Type</div>
                    <div className="font-medium capitalize">{selectedCrisis.type.replace('_', ' ')}</div>
                  </div>
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider text-ink-500 mb-1">Status</div>
                    <div className={cn(
                      "text-xs px-2 py-1 border inline-block",
                      getStatusColor(selectedCrisis.status)
                    )}>
                      {selectedCrisis.status.toUpperCase()}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs font-mono uppercase tracking-wider text-ink-500 mb-1">Agencies</div>
                    <div className="text-sm text-ink-600">
                      {selectedCrisis.response_agencies?.join(', ') || 'UN OCHA, Red Cross'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Activity className="h-8 w-8 text-ink-400 mb-2 mx-auto" />
              <div className="text-sm text-ink-500">
                Select a crisis to view details
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Alerts Tab Component
const AlertsTab: React.FC<{
  alerts: Alert[];
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ alerts, viewLevel, isMobileView }) => {
  const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending'>('all');

  const filteredAlerts = alerts.filter(alert => {
    const priorityMatch = filter === 'all' || alert.priority === filter;
    const statusMatch = statusFilter === 'all' || alert.status.toLowerCase() === statusFilter;
    return priorityMatch && statusMatch;
  });

  const getConfidenceBar = (confidence: number) => (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 bg-ink-200">
        <div 
          className={cn("h-full", getConfidenceColor(confidence))}
          style={{ width: `${confidence}%` }}
        />
      </div>
      <span className="text-xs font-mono">{confidence}%</span>
    </div>
  );

  const getSeverityBar = (severity: number) => (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 bg-ink-200">
        <div 
          className="h-full bg-accent-500"
          style={{ width: `${severity}%` }}
        />
      </div>
      <span className="text-xs font-mono">{severity}%</span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Alert Filters */}
      <div className="flex flex-wrap gap-4 pb-4 border-b border-ink-200">
        <div className="flex items-center gap-2">
          <label className="text-xs font-mono uppercase tracking-ultra text-ink-500">Priority</label>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value as any)}
            className="input-minimal py-1 text-sm"
          >
            <option value="all">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <label className="text-xs font-mono uppercase tracking-ultra text-ink-500">Status</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="input-minimal py-1 text-sm"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs font-mono text-ink-500">{filteredAlerts.length} alerts</span>
        </div>
      </div>

      {/* Alerts Table */}
      <div className="card-minimal overflow-hidden">
        {isMobileView ? (
          <div className="divide-y divide-ink-200">
            {filteredAlerts.map((alert) => (
              <div key={alert.event_id} className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm">{alert.event_id}</span>
                  <div className="flex items-center gap-2">
                    <div className={cn("w-2 h-2 rounded-full", getPriorityColor(alert.priority))} />
                    <span className={cn(
                      "text-xs px-2 py-1 border",
                      getStatusColor(alert.status)
                    )}>
                      {alert.status}
                    </span>
                  </div>
                </div>
                
                <div>
                  <div className="text-sm font-medium text-ink-900">{alert.crisis_type}</div>
                  <div className="text-xs text-ink-600">{alert.location}</div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-ink-500 mb-1">Confidence</div>
                    {getConfidenceBar(alert.confidence)}
                  </div>
                  <div>
                    <div className="text-xs text-ink-500 mb-1">Severity</div>
                    {getSeverityBar(alert.severity)}
                  </div>
                </div>
                
                <div className="text-xs text-ink-500">
                  {formatRelativeTime(new Date(alert.detection_time))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-ink-200">
                <tr>
                  <th className="text-left py-4 px-4 text-xs font-mono uppercase tracking-wider">Event ID</th>
                  <th className="text-left py-4 px-4 text-xs font-mono uppercase tracking-wider">Time</th>
                  <th className="text-left py-4 px-4 text-xs font-mono uppercase tracking-wider">Status</th>
                  <th className="text-left py-4 px-4 text-xs font-mono uppercase tracking-wider">Type</th>
                  <th className="text-left py-4 px-4 text-xs font-mono uppercase tracking-wider">Location</th>
                  <th className="text-left py-4 px-4 text-xs font-mono uppercase tracking-wider">Confidence</th>
                  <th className="text-left py-4 px-4 text-xs font-mono uppercase tracking-wider">Severity</th>
                  {viewLevel !== 'primary' && (
                    <th className="text-left py-4 px-4 text-xs font-mono uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {filteredAlerts.map((alert) => (
                  <tr key={alert.event_id} className="hover:bg-ink-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className={cn("w-2 h-2 rounded-full", getPriorityColor(alert.priority))} />
                        <span className="font-mono text-sm">{alert.event_id}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-ink-600">
                      {formatRelativeTime(new Date(alert.detection_time))}
                    </td>
                    <td className="py-3 px-4">
                      <span className={cn(
                        "text-xs px-2 py-1 border",
                        getStatusColor(alert.status)
                      )}>
                        {alert.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">{alert.crisis_type}</td>
                    <td className="py-3 px-4 text-sm text-ink-600">{alert.location}</td>
                    <td className="py-3 px-4">{getConfidenceBar(alert.confidence)}</td>
                    <td className="py-3 px-4">{getSeverityBar(alert.severity)}</td>
                    {viewLevel !== 'primary' && (
                      <td className="py-3 px-4">
                        <button className="btn btn-ghost text-xs px-2 py-1">
                          Investigate
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Communications Section Component
const CommunicationsSection: React.FC<{
  communications: Communication[];
  expanded: boolean;
  onToggle: () => void;
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ communications, expanded, onToggle, viewLevel, isMobileView }) => {
  const displayCount = expanded ? communications.length : 3;
  const displayComms = communications.slice(0, displayCount);

  const getCommIcon = (type: string) => {
    switch (type) {
      case 'sitrep': return '📊';
      case 'coordination': return '📞';
      case 'public': return '📢';
      case 'internal': return '📝';
      default: return '📧';
    }
  };

  const getCommColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-accent-300 bg-accent-50';
      case 'medium': return 'border-ocean-300 bg-ocean-50';
      case 'low': return 'border-sage-300 bg-sage-50';
      default: return 'border-ink-300 bg-ink-50';
    }
  };

  const getCommStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'text-sage-600 bg-sage-100 border-sage-300';
      case 'draft': return 'text-sand-600 bg-sand-100 border-sand-300';
      case 'pending': return 'text-ocean-600 bg-ocean-100 border-ocean-300';
      default: return 'text-ink-600 bg-ink-100 border-ink-300';
    }
  };

  return (
    <div className="card-minimal p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display text-ink-900 flex items-center gap-2">
          <Radio className="w-4 h-4 inline mr-2" />Communications
          <span className="text-xs font-mono text-ink-500 bg-ink-100 px-2 py-1">{communications.length}</span>
        </h3>
        {communications.length > 3 && (
          <button 
            onClick={onToggle}
            className="text-sm text-accent-600 hover:text-accent-700 transition-colors"
          >
            {expanded ? 'Show Less' : 'View All'}
          </button>
        )}
      </div>

      <div className="space-y-3 max-h-80 overflow-y-auto">
        {displayComms.map((comm) => (
          <div key={comm.id} className={cn(
            "p-4 border-2",
            getCommColor(comm.priority)
          )}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg">{getCommIcon(comm.type)}</span>
                <h4 className="font-medium text-ink-900 text-sm">{comm.title}</h4>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-xs px-2 py-1 border",
                  getCommStatusColor(comm.status)
                )}>
                  {comm.status.toUpperCase()}
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between text-xs text-ink-600">
              <span>{comm.sender}</span>
              <span>{formatRelativeTime(new Date(comm.timestamp))}</span>
            </div>
            
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs font-mono text-ink-500 capitalize">{comm.type.replace('_', ' ')}</span>
              <div className={cn(
                "w-2 h-2",
                comm.priority === 'high' ? 'bg-accent-500' :
                comm.priority === 'medium' ? 'bg-ocean-500' : 'bg-sage-500'
              )} />
            </div>
          </div>
        ))}
      </div>
      
      {viewLevel !== 'primary' && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-ink-200">
          <button className="btn btn-ghost text-sm flex-1">
            📝 New Message
          </button>
          <button className="btn btn-ghost text-sm flex-1">
            📊 Send Report
          </button>
        </div>
      )}
    </div>
  );
};

// TimelineSection Component
const TimelineSection: React.FC<{
  timeline: TimelineEvent[];
  expanded: boolean;
  onToggle: () => void;
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ timeline, expanded, onToggle, viewLevel, isMobileView }) => {
  const displayCount = expanded ? timeline.length : 5;
  const displayEvents = timeline.slice(0, displayCount);

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'alert': return '🚨';
      case 'decision': return '✅';
      case 'response': return '📦';
      case 'update': return '📄';
      case 'incident': return '⚡';
      default: return '📍';
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'alert': return 'border-accent-300 bg-accent-50';
      case 'decision': return 'border-sage-300 bg-sage-50';
      case 'response': return 'border-ocean-300 bg-ocean-50';
      case 'update': return 'border-sand-300 bg-sand-50';
      case 'incident': return 'border-accent-500 bg-accent-100';
      default: return 'border-ink-300 bg-ink-50';
    }
  };

  return (
    <div className="card-minimal p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display text-ink-900 flex items-center gap-2">
          🕰️ Timeline
          <span className="text-xs font-mono text-ink-500 bg-ink-100 px-2 py-1">{timeline.length}</span>
        </h3>
        {timeline.length > 5 && (
          <button 
            onClick={onToggle}
            className="text-sm text-accent-600 hover:text-accent-700 transition-colors"
          >
            {expanded ? 'Show Less' : 'View All'}
          </button>
        )}
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {displayEvents.map((event, index) => (
          <div key={event.id} className="flex gap-3">
            <div className="flex flex-col items-center flex-shrink-0">
              <div className={cn(
                "w-8 h-8 border-2 flex items-center justify-center text-sm",
                getEventColor(event.type)
              )}>
                {getEventIcon(event.type)}
              </div>
              {index < displayEvents.length - 1 && (
                <div className="w-0.5 h-8 bg-ink-200 mt-1"></div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-1">
                <h4 className="font-medium text-ink-900 text-sm leading-tight">{event.title}</h4>
                <span className="text-xs text-ink-500 flex-shrink-0 ml-2">
                  {formatRelativeTime(new Date(event.timestamp))}
                </span>
              </div>
              <p className="text-sm text-ink-600 leading-relaxed mb-2">{event.description}</p>
              <div className="flex items-center gap-3 text-xs">
                {event.actor && (
                  <span className="text-ink-500">
                    <span className="font-mono">By:</span> {event.actor}
                  </span>
                )}
                {event.severity && (
                  <span className={cn(
                    "px-2 py-0.5 border",
                    getSeverityColorClass(event.severity)
                  )}>
                    Severity {event.severity}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {!expanded && timeline.length > 5 && (
        <div className="text-center mt-4 pt-4 border-t border-ink-200">
          <span className="text-xs text-ink-500">Showing recent {displayCount} of {timeline.length} events</span>
        </div>
      )}
    </div>
  );
};

// DecisionsSection Component
const DecisionsSection: React.FC<{
  decisions: Decision[];
  selectedCrisis: MockCrisis | null;
  expanded: boolean;
  onToggle: () => void;
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ decisions, selectedCrisis, expanded, onToggle, viewLevel, isMobileView }) => {
  const displayCount = expanded ? decisions.length : 4;
  const displayDecisions = decisions.slice(0, displayCount);

  const getDecisionIcon = (type: string) => {
    switch (type) {
      case 'immediate': return '⚡';
      case 'strategic': return '🎯';
      case 'tactical': return '📋';
      default: return '✅';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-sage-600';
    if (confidence >= 60) return 'text-ocean-600';
    return 'text-accent-600';
  };

  const getDecisionColor = (status: string, priority: string) => {
    if (status === 'active' && priority === 'high') return 'border-accent-500 bg-accent-50';
    if (status === 'active') return 'border-ocean-300 bg-ocean-50';
    if (status === 'pending') return 'border-sand-300 bg-sand-50';
    if (status === 'completed') return 'border-sage-300 bg-sage-50';
    return 'border-ink-300 bg-ink-50';
  };

  return (
    <div className="space-y-6">
      {/* Active/Pending Decisions */}
      <div className="card-minimal p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-display text-ink-900 flex items-center gap-2">
            <Zap className="w-4 h-4 inline mr-2" />Decisions
            <span className="text-xs font-mono text-ink-500 bg-ink-100 px-2 py-1">{decisions.length}</span>
          </h3>
          {decisions.length > 4 && (
            <button 
              onClick={onToggle}
              className="text-sm text-accent-600 hover:text-accent-700 transition-colors"
            >
              {expanded ? 'Show Less' : 'View All'}
            </button>
          )}
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {displayDecisions.map((decision) => (
            <div key={decision.id} className={cn(
              "p-4 border-2",
              getDecisionColor(decision.status, decision.priority)
            )}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getDecisionIcon(decision.type)}</span>
                  <h4 className="font-medium text-ink-900 text-sm">{decision.title}</h4>
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "text-xs px-2 py-1 border",
                    decision.status === 'active' ? 'bg-ocean-100 text-ocean-700 border-ocean-300' :
                    decision.status === 'pending' ? 'bg-sand-100 text-sand-700 border-sand-300' :
                    'bg-sage-100 text-sage-700 border-sage-300'
                  )}>
                    {decision.status.toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="text-ink-500 mb-1">Type</div>
                  <div className="font-medium capitalize">{decision.type}</div>
                </div>
                <div>
                  <div className="text-ink-500 mb-1">Confidence</div>
                  <div className={cn("font-medium", getConfidenceColor(decision.confidence))}>
                    {decision.confidence}%
                  </div>
                </div>
                <div>
                  <div className="text-ink-500 mb-1">Priority</div>
                  <div className={cn(
                    "font-medium capitalize",
                    decision.priority === 'high' ? 'text-accent-600' :
                    decision.priority === 'medium' ? 'text-ocean-600' : 'text-sage-600'
                  )}>
                    {decision.priority}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-2 text-xs text-ink-600">
                <span>{decision.assignee || 'Unassigned'}</span>
                <span>{formatRelativeTime(new Date(decision.timestamp))}</span>
              </div>
              
              {decision.deadline && decision.status !== 'completed' && (
                <div className="text-xs text-accent-600 mt-1">
                  Due: {formatRelativeTime(new Date(decision.deadline))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Quick Decision Making */}
      {selectedCrisis && viewLevel !== 'primary' && (
        <div className="card-minimal p-6">
          <h4 className="font-medium text-ink-900 mb-3">Make New Decision</h4>
          <div className="text-sm text-ink-600 mb-4">
            For: <span className="font-medium">{selectedCrisis.title}</span>
          </div>
          
          <div className="space-y-3">
            <button className="btn btn-ghost w-full text-left justify-start">
              <Zap className="w-4 h-4 inline mr-2" />Deploy emergency resources
            </button>
            <button className="btn btn-ghost w-full text-left justify-start">
              🎯 Establish coordination hub
            </button>
            <button className="btn btn-ghost w-full text-left justify-start">
              <CheckCircle className="w-4 h-4 inline mr-2" />Request additional assessment
            </button>
          </div>
          
          <button className="btn btn-primary w-full mt-4">
            Open Decision Wizard
          </button>
        </div>
      )}
    </div>
  );
};

// ResourcesSection Component  
const ResourcesSection: React.FC<{
  resources: Resource[];
  expanded: boolean;
  onToggle: () => void;
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ resources, expanded, onToggle, viewLevel, isMobileView }) => {
  const displayCount = expanded ? resources.length : 4;
  const displayResources = resources.slice(0, displayCount);

  const getResourceIcon = (type: string) => {
    switch (type) {
      case 'personnel': return '👥';
      case 'equipment': return '🔧';
      case 'supplies': return '📦';
      default: return '📋';
    }
  };

  const getResourceStatusColor = (status: string) => {
    switch (status) {
      case 'adequate': return 'text-sage-600 bg-sage-100 border-sage-300';
      case 'low': return 'text-sand-600 bg-sand-100 border-sand-300';
      case 'critical': return 'text-accent-600 bg-accent-100 border-accent-300';
      default: return 'text-ink-600 bg-ink-100 border-ink-300';
    }
  };

  const utilizationRate = (deployed: number, total: number) => {
    return Math.round((deployed / total) * 100);
  };

  return (
    <div className="card-minimal p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display text-ink-900 flex items-center gap-2">
          <Package className="w-4 h-4 inline mr-2" />Resources
          <span className="text-xs font-mono text-ink-500 bg-ink-100 px-2 py-1">{resources.length}</span>
        </h3>
        {resources.length > 4 && (
          <button 
            onClick={onToggle}
            className="text-sm text-accent-600 hover:text-accent-700 transition-colors"
          >
            {expanded ? 'Show Less' : 'View All'}
          </button>
        )}
      </div>

      <div className="space-y-3 max-h-80 overflow-y-auto">
        {displayResources.map((resource) => (
          <div key={resource.id} className="border border-ink-200 p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg">{getResourceIcon(resource.type)}</span>
                <h4 className="font-medium text-ink-900 text-sm">{resource.name}</h4>
              </div>
              <span className={cn(
                "text-xs px-2 py-1 border",
                getResourceStatusColor(resource.status)
              )}>
                {resource.status.toUpperCase()}
              </span>
            </div>
            
            <div className="grid grid-cols-3 gap-3 text-xs mb-3">
              <div>
                <div className="text-ink-500 mb-1">Total</div>
                <div className="font-medium">{resource.total}</div>
              </div>
              <div>
                <div className="text-ink-500 mb-1">Deployed</div>
                <div className="font-medium">{resource.deployed}</div>
              </div>
              <div>
                <div className="text-ink-500 mb-1">Available</div>
                <div className="font-medium">{resource.available}</div>
              </div>
            </div>
            
            {/* Utilization Bar */}
            <div className="mb-2">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-ink-500">Utilization</span>
                <span className="font-medium">{utilizationRate(resource.deployed, resource.total)}%</span>
              </div>
              <div className="w-full h-2 bg-ink-200">
                <div 
                  className="h-full bg-ocean-500"
                  style={{ width: `${utilizationRate(resource.deployed, resource.total)}%` }}
                />
              </div>
            </div>
            
            {resource.location && (
              <div className="text-xs text-ink-500">
                📍 {resource.location}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {viewLevel !== 'primary' && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-ink-200">
          <button className="btn btn-ghost text-sm flex-1">
            <CheckCircle className="w-4 h-4 inline mr-2" />Request Resources
          </button>
          <button className="btn btn-ghost text-sm flex-1">
            🚛 Deploy Assets
          </button>
        </div>
      )}
    </div>
  );
};

// DocumentsSection Component
const DocumentsSection: React.FC<{
  documents: Document[];
  expanded: boolean;
  onToggle: () => void;
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ documents, expanded, onToggle, viewLevel, isMobileView }) => {
  const displayCount = expanded ? documents.length : 4;
  const displayDocs = documents.slice(0, displayCount);

  const getDocIcon = (type: string) => {
    switch (type) {
      case 'assessment': return '📊';
      case 'plan': return '📋';
      case 'report': return '📄';
      case 'briefing': return '📝';
      default: return '📄';
    }
  };

  const getDocStatusColor = (status: string) => {
    switch (status) {
      case 'final': return 'text-sage-600 bg-sage-100 border-sage-300';
      case 'review': return 'text-ocean-600 bg-ocean-100 border-ocean-300';
      case 'draft': return 'text-sand-600 bg-sand-100 border-sand-300';
      default: return 'text-ink-600 bg-ink-100 border-ink-300';
    }
  };

  return (
    <div className="card-minimal p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display text-ink-900 flex items-center gap-2">
          <FileText className="w-4 h-4 inline mr-2" />Documents
          <span className="text-xs font-mono text-ink-500 bg-ink-100 px-2 py-1">{documents.length}</span>
        </h3>
        {documents.length > 4 && (
          <button 
            onClick={onToggle}
            className="text-sm text-accent-600 hover:text-accent-700 transition-colors"
          >
            {expanded ? 'Show Less' : 'View All'}
          </button>
        )}
      </div>

      <div className="space-y-3 max-h-80 overflow-y-auto">
        {displayDocs.map((doc) => (
          <div key={doc.id} className="border border-ink-200 p-4 hover:bg-ink-25 transition-colors cursor-pointer">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-lg flex-shrink-0">{getDocIcon(doc.type)}</span>
                <h4 className="font-medium text-ink-900 text-sm leading-tight truncate">{doc.title}</h4>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={cn(
                  "text-xs px-2 py-1 border",
                  getDocStatusColor(doc.status)
                )}>
                  {doc.status.toUpperCase()}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-ink-500 mb-1">Type</div>
                <div className="font-medium capitalize">{doc.type}</div>
              </div>
              <div>
                <div className="text-ink-500 mb-1">Size</div>
                <div className="font-medium">{doc.size}</div>
              </div>
            </div>
            
            <div className="flex items-center justify-between mt-2 text-xs text-ink-600">
              <span>{doc.author}</span>
              <span>{formatRelativeTime(new Date(doc.lastModified))}</span>
            </div>
          </div>
        ))}
      </div>
      
      {viewLevel !== 'primary' && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-ink-200">
          <button className="btn btn-ghost text-sm flex-1">
            📤 Upload Document
          </button>
          <button className="btn btn-ghost text-sm flex-1">
            📝 Create Report
          </button>
        </div>
      )}
    </div>
  );
};

// Timeline Tab Component  
const TimelineTab: React.FC<{
  timeline: TimelineEvent[];
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ timeline, viewLevel, isMobileView }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-display text-ink-900">Recent Activity</h3>
        <div className="text-xs text-ink-500">{timeline.length} events in last 24h</div>
      </div>

      <div className="space-y-4">
        {timeline.map((event, index) => (
          <div key={event.id} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className={cn(
                "w-8 h-8 border-2 bg-white flex items-center justify-center text-sm",
                getEventColor(event.type)
              )}>
                {getEventIcon(event.type)}
              </div>
              {index < timeline.length - 1 && (
                <div className="w-0.5 h-12 bg-ink-200 mt-2"></div>
              )}
            </div>
            
            <div className="flex-1 pb-8">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-medium text-ink-900">{event.title}</h4>
                <span className="text-xs text-ink-500">
                  {formatRelativeTime(new Date(event.timestamp))}
                </span>
              </div>
              <p className="text-sm text-ink-600 mb-2">{event.description}</p>
              {event.actor && (
                <div className="text-xs text-ink-500">
                  <span className="font-mono">By:</span> {event.actor}
                </div>
              )}
              {event.severity && (
                <span className={cn(
                  "text-xs px-2 py-1 border mt-2 inline-block",
                  getSeverityColorClass(event.severity)
                )}>
                  Severity {event.severity}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Decisions Tab Component
const DecisionsTab: React.FC<{
  selectedCrisis: MockCrisis | null;
  stats: MockStats;
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ selectedCrisis, stats, viewLevel, isMobileView }) => {
  const [selectedDecision, setSelectedDecision] = useState<string>('');
  const [confidence, setConfidence] = useState(75);
  const [rationale, setRationale] = useState('');

  const mockDecisions = [
    { id: 'DEC-001', title: 'Emergency Shelter Deployment', confidence: 92, status: 'Completed', timestamp: '2 hours ago' },
    { id: 'DEC-002', title: 'Medical Supply Distribution', confidence: 88, status: 'In Progress', timestamp: '4 hours ago' },
    { id: 'DEC-003', title: 'Evacuation Route Planning', confidence: 76, status: 'Pending', timestamp: '6 hours ago' },
    { id: 'DEC-004', title: 'Resource Coordination', confidence: 95, status: 'Completed', timestamp: '8 hours ago' }
  ];

  const getDecisionOptions = (crisisType?: string) => {
    const options = {
      flood: {
        A: 'Immediate evacuation of flood-prone areas',
        B: 'Deploy emergency shelters and relief supplies', 
        C: 'Monitor water levels and prepare contingency plans'
      },
      conflict: {
        A: 'Negotiate humanitarian access corridors',
        B: 'Establish protected civilian safe zones',
        C: 'Coordinate remote humanitarian assistance'
      },
      drought: {
        A: 'Deploy mobile water treatment units',
        B: 'Distribute emergency food and nutrition supplies',
        C: 'Coordinate with local water management authorities'
      },
      default: {
        A: 'Deploy immediate emergency response teams',
        B: 'Coordinate with local emergency services',
        C: 'Monitor situation and prepare scaled response'
      }
    };

    return options[crisisType as keyof typeof options] || options.default;
  };

  const decisionOptions = selectedCrisis ? getDecisionOptions(selectedCrisis.type) : getDecisionOptions();

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 80) return 'High Confidence';
    if (confidence >= 60) return 'Moderate Confidence';
    if (confidence >= 40) return 'Low Confidence';
    return 'Very Low Confidence';
  };

  return (
    <div className="space-y-6">
      {/* Decision History */}
      <div className="card-minimal p-6">
        <h3 className="text-lg font-display text-ink-900 mb-4">Recent Decisions</h3>
        <div className="space-y-3">
          {mockDecisions.map((decision) => (
            <div key={decision.id} className="flex items-center justify-between p-3 border border-ink-200">
              <div className="flex-1">
                <div className="font-medium text-ink-900 text-sm">{decision.title}</div>
                <div className="flex items-center gap-4 mt-1">
                  <span className="text-xs font-mono text-ink-500">{decision.id}</span>
                  <span className={cn(
                    "text-xs px-2 py-1 border",
                    decision.status === 'Completed' ? 'bg-sage-100 text-sage-700 border-sage-300' :
                    decision.status === 'In Progress' ? 'bg-ocean-100 text-ocean-700 border-ocean-300' :
                    'bg-sand-100 text-sand-700 border-sand-300'
                  )}>
                    {decision.status}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className={cn("text-sm font-medium", getConfidenceColor(decision.confidence))}>
                  {decision.confidence}%
                </div>
                <div className="text-xs text-ink-500">{decision.timestamp}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Make New Decision */}
      {selectedCrisis && viewLevel !== 'primary' && (
        <div className="card-minimal p-6">
          <h3 className="text-lg font-display text-ink-900 mb-4">Make Decision</h3>
          
          <div className="mb-6">
            <h4 className="font-medium text-ink-900 mb-2">{selectedCrisis.title}</h4>
            <div className="text-sm text-ink-600 mb-4">{selectedCrisis.description}</div>
            <div className="flex gap-4 text-xs text-ink-500">
              <span>Location: {selectedCrisis.location}</span>
              <span>Affected: {formatNumber(selectedCrisis.affected_population)}</span>
              <span>Severity: {selectedCrisis.severity}/5</span>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-ink-900 mb-3">Available Options</h4>
              <div className="space-y-3">
                {Object.entries(decisionOptions).map(([key, option]) => (
                  <div 
                    key={key}
                    className={cn(
                      "p-4 border-2 cursor-pointer transition-all",
                      selectedDecision === key ? 'border-accent-300 bg-accent-50' : 'border-ink-200 hover:border-accent-200'
                    )}
                    onClick={() => setSelectedDecision(key)}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="decision"
                        value={key}
                        checked={selectedDecision === key}
                        onChange={() => setSelectedDecision(key)}
                        className="mt-1"
                      />
                      <div>
                        <h5 className="font-medium text-ink-900">Option {key}</h5>
                        <p className="text-sm text-ink-600 mt-1">{option}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-ink-900 mb-3">Confidence Level</h4>
              <div className="space-y-3">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={confidence}
                  onChange={(e) => setConfidence(parseInt(e.target.value))}
                  className="w-full h-2 bg-ink-200 appearance-none slider"
                />
                <div className="flex justify-between items-center">
                  <span className={cn("text-sm font-medium", getConfidenceColor(confidence))}>
                    {confidence}% - {getConfidenceLabel(confidence)}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-ink-900 mb-3">Rationale</h4>
              <textarea
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                placeholder="Explain your decision rationale..."
                className="input-minimal h-24 resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button 
                disabled={!selectedDecision || !rationale.trim()}
                className={cn(
                  "btn btn-primary flex-1",
                  (!selectedDecision || !rationale.trim()) && "opacity-50 cursor-not-allowed"
                )}
              >
                Submit Decision
              </button>
              <button className="btn btn-ghost">
                Save Draft
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decision Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-minimal p-4 text-center">
          <div className="text-2xl font-bold text-ink-900">{stats.totalDecisions || 0}</div>
          <div className="text-xs font-mono uppercase tracking-wider text-ink-500">Total Decisions</div>
        </div>
        <div className="card-minimal p-4 text-center">
          <div className="text-2xl font-bold text-ink-900">{stats.pendingDecisions || 0}</div>
          <div className="text-xs font-mono uppercase tracking-wider text-ink-500">Pending</div>
        </div>
        <div className="card-minimal p-4 text-center">
          <div className="text-2xl font-bold text-sage-600">{stats.averageConfidence || 0}%</div>
          <div className="text-xs font-mono uppercase tracking-wider text-ink-500">Avg Confidence</div>
        </div>
      </div>
    </div>
  );
};

// Resources Tab Component
const ResourcesTab: React.FC<{
  crises: MockCrisis[];
  stats: MockStats;
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ crises, stats, viewLevel, isMobileView }) => {
  const resources = [
    { type: 'Emergency Shelters', available: 450, deployed: 280, unit: 'units', status: 'adequate' },
    { type: 'Medical Supplies', available: 15, deployed: 8, unit: 'kits', status: 'low' },
    { type: 'Food Packages', available: 2400, deployed: 1650, unit: 'packages', status: 'adequate' },
    { type: 'Water Purification', available: 12, deployed: 9, unit: 'units', status: 'critical' },
    { type: 'Personnel', available: 45, deployed: 32, unit: 'staff', status: 'adequate' },
    { type: 'Transport Vehicles', available: 18, deployed: 14, unit: 'vehicles', status: 'low' }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'adequate': return 'bg-sage-100 text-sage-700 border-sage-300';
      case 'low': return 'bg-sand-100 text-sand-700 border-sand-300';
      case 'critical': return 'bg-accent-100 text-accent-700 border-accent-300';
      default: return 'bg-ink-100 text-ink-700 border-ink-300';
    }
  };

  const utilizationRate = (deployed: number, available: number) => {
    return Math.round((deployed / available) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Resource Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card-minimal p-4 text-center">
          <div className="text-2xl font-bold text-ink-900">{stats.resourcesDeployed || 0}</div>
          <div className="text-xs font-mono uppercase tracking-wider text-ink-500">Resources Deployed</div>
        </div>
        <div className="card-minimal p-4 text-center">
          <div className="text-2xl font-bold text-ocean-600">{stats.responsesActive || 0}</div>
          <div className="text-xs font-mono uppercase tracking-wider text-ink-500">Active Responses</div>
        </div>
        <div className="card-minimal p-4 text-center">
          <div className="text-2xl font-bold text-sage-600">85%</div>
          <div className="text-xs font-mono uppercase tracking-wider text-ink-500">Avg Utilization</div>
        </div>
        <div className="card-minimal p-4 text-center">
          <div className="text-2xl font-bold text-accent-600">3</div>
          <div className="text-xs font-mono uppercase tracking-wider text-ink-500">Critical Shortages</div>
        </div>
      </div>

      {/* Resource Status Table */}
      <div className="card-minimal overflow-hidden">
        <div className="px-6 py-4 border-b border-ink-200">
          <h3 className="text-lg font-display text-ink-900">Resource Status</h3>
        </div>
        
        {isMobileView ? (
          <div className="divide-y divide-ink-200">
            {resources.map((resource) => (
              <div key={resource.type} className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-ink-900">{resource.type}</h4>
                  <span className={cn("text-xs px-2 py-1 border", getStatusColor(resource.status))}>
                    {resource.status.toUpperCase()}
                  </span>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-ink-500">Available</div>
                    <div className="font-medium">{resource.available} {resource.unit}</div>
                  </div>
                  <div>
                    <div className="text-xs text-ink-500">Deployed</div>
                    <div className="font-medium">{resource.deployed} {resource.unit}</div>
                  </div>
                  <div>
                    <div className="text-xs text-ink-500">Utilization</div>
                    <div className="font-medium">{utilizationRate(resource.deployed, resource.available)}%</div>
                  </div>
                </div>
                
                <div className="w-full h-2 bg-ink-200">
                  <div 
                    className="h-full bg-ocean-500"
                    style={{ width: `${utilizationRate(resource.deployed, resource.available)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-ink-200">
                <tr>
                  <th className="text-left py-4 px-6 text-xs font-mono uppercase tracking-wider">Resource Type</th>
                  <th className="text-left py-4 px-6 text-xs font-mono uppercase tracking-wider">Available</th>
                  <th className="text-left py-4 px-6 text-xs font-mono uppercase tracking-wider">Deployed</th>
                  <th className="text-left py-4 px-6 text-xs font-mono uppercase tracking-wider">Utilization</th>
                  <th className="text-left py-4 px-6 text-xs font-mono uppercase tracking-wider">Status</th>
                  {viewLevel !== 'primary' && (
                    <th className="text-left py-4 px-6 text-xs font-mono uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {resources.map((resource) => (
                  <tr key={resource.type} className="hover:bg-ink-50">
                    <td className="py-4 px-6 font-medium text-ink-900">{resource.type}</td>
                    <td className="py-4 px-6 text-ink-600">{resource.available} {resource.unit}</td>
                    <td className="py-4 px-6 text-ink-600">{resource.deployed} {resource.unit}</td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-ink-200">
                          <div 
                            className="h-full bg-ocean-500"
                            style={{ width: `${utilizationRate(resource.deployed, resource.available)}%` }}
                          />
                        </div>
                        <span className="text-sm">{utilizationRate(resource.deployed, resource.available)}%</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={cn("text-xs px-2 py-1 border", getStatusColor(resource.status))}>
                        {resource.status.toUpperCase()}
                      </span>
                    </td>
                    {viewLevel !== 'primary' && (
                      <td className="py-4 px-6">
                        <button className="btn btn-ghost text-xs px-3 py-1">
                          Request More
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Crisis Resource Allocation */}
      <div className="card-minimal p-6">
        <h3 className="text-lg font-display text-ink-900 mb-4">Crisis Resource Allocation</h3>
        <div className="space-y-4">
          {crises.map((crisis) => (
            <div key={crisis.id} className="border border-ink-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-ink-900">{crisis.title}</h4>
                <span className={cn("text-xs px-2 py-1 border", getSeverityColorClass(crisis.severity))}>
                  Priority {crisis.severity}
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-xs text-ink-500">Shelters</div>
                  <div className="font-medium">85 units</div>
                </div>
                <div>
                  <div className="text-xs text-ink-500">Medical</div>
                  <div className="font-medium">3 kits</div>
                </div>
                <div>
                  <div className="text-xs text-ink-500">Food</div>
                  <div className="font-medium">520 packages</div>
                </div>
                <div>
                  <div className="text-xs text-ink-500">Personnel</div>
                  <div className="font-medium">12 staff</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Knowledge Access Tab Component
const KnowledgeTab: React.FC<{
  query: string;
  setQuery: (query: string) => void;
  selectedCrisis: MockCrisis | null;
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ query, setQuery, selectedCrisis, viewLevel, isMobileView }) => {
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const knowledgeCategories = [
    { id: 'all', name: 'All Resources', count: 1247 },
    { id: 'sphere', name: 'Sphere Standards', count: 156 },
    { id: 'procedures', name: 'NRC Procedures', count: 89 },
    { id: 'lessons', name: 'Lessons Learned', count: 234 },
    { id: 'technical', name: 'Technical Guides', count: 178 },
    { id: 'coordination', name: 'Coordination Protocols', count: 67 }
  ];

  const mockResults = [
    {
      id: 1,
      title: 'Emergency Response in Flood Situations',
      category: 'Sphere Standards',
      relevance: 95,
      snippet: 'Key standards for emergency shelter provision during flood emergencies, including site selection criteria and basic infrastructure requirements...',
      source: 'Sphere Handbook 2018, Chapter 4.2',
      lastUpdated: '2023-10-15'
    },
    {
      id: 2,
      title: 'NRC Flood Response Procedures',
      category: 'NRC Procedures',
      relevance: 87,
      snippet: 'Standard operating procedures for NRC teams responding to flood emergencies, including assessment protocols and coordination mechanisms...',
      source: 'NRC Emergency Response Manual v3.2',
      lastUpdated: '2024-01-20'
    },
    {
      id: 3,
      title: 'Bangladesh Flood Response - Lessons Learned',
      category: 'Lessons Learned',
      relevance: 82,
      snippet: 'Analysis of 2020 Bangladesh monsoon response highlighting key challenges in shelter provision and community coordination...',
      source: 'Post-Response Evaluation Report',
      lastUpdated: '2023-08-10'
    }
  ];

  const handleSearch = async () => {
    if (!query.trim()) return;
    
    setIsSearching(true);
    
    // Simulate search delay
    setTimeout(() => {
      setSearchResults(mockResults);
      setIsSearching(false);
    }, 1000);
  };

  const getRelevanceColor = (relevance: number) => {
    if (relevance >= 90) return 'text-sage-600';
    if (relevance >= 70) return 'text-ocean-600';
    return 'text-sand-600';
  };

  // Contextual suggestions based on selected crisis
  const getContextualSuggestions = () => {
    if (!selectedCrisis) return [];
    
    const suggestions = {
      flood: [
        'Flood emergency shelter standards',
        'Water and sanitation in flood response',
        'Community coordination during floods',
        'Flood early warning systems'
      ],
      conflict: [
        'Humanitarian access in conflict zones',
        'Protection of civilians procedures',
        'Conflict-sensitive programming',
        'Safety and security protocols'
      ],
      drought: [
        'Emergency water provision standards',
        'Malnutrition response protocols',
        'Livestock support in drought',
        'Community resilience building'
      ]
    };
    
    return suggestions[selectedCrisis.type as keyof typeof suggestions] || [];
  };

  return (
    <div className="space-y-6">
      {/* Knowledge Search */}
      <div className="card-minimal p-6">
        <h3 className="text-lg font-display text-ink-900 mb-4">Knowledge Access System</h3>
        
        <div className="space-y-4">
          <div className="flex gap-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask about humanitarian standards, procedures, or best practices..."
              className="input-minimal flex-1"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button 
              onClick={handleSearch}
              disabled={isSearching || !query.trim()}
              className={cn(
                "btn btn-primary px-6",
                (isSearching || !query.trim()) && "opacity-50 cursor-not-allowed"
              )}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            {knowledgeCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={cn(
                  "text-xs px-3 py-2 border transition-all",
                  selectedCategory === category.id
                    ? "border-accent-300 bg-accent-50 text-accent-700"
                    : "border-ink-300 text-ink-600 hover:border-accent-200"
                )}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contextual Suggestions */}
      {selectedCrisis && (
        <div className="card-minimal p-6">
          <h4 className="font-medium text-ink-900 mb-3">
            Suggested for {selectedCrisis.type.replace('_', ' ')} response
          </h4>
          <div className="space-y-2">
            {getContextualSuggestions().map((suggestion, index) => (
              <button
                key={index}
                onClick={() => {
                  setQuery(suggestion);
                  handleSearch();
                }}
                className="block text-left text-sm text-accent-600 hover:text-accent-700 transition-colors"
              >
                → {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-ink-900">Search Results</h4>
            <span className="text-xs text-ink-500">{searchResults.length} results found</span>
          </div>
          
          {searchResults.map((result) => (
            <div key={result.id} className="card-minimal p-6">
              <div className="flex items-start justify-between mb-3">
                <h5 className="font-medium text-ink-900 text-lg">{result.title}</h5>
                <div className="flex items-center gap-2">
                  <span className={cn("text-sm font-medium", getRelevanceColor(result.relevance))}>
                    {result.relevance}%
                  </span>
                  <span className="text-xs text-ink-500">relevance</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs px-2 py-1 bg-ocean-100 text-ocean-700 border border-ocean-300">
                  {result.category}
                </span>
                <span className="text-xs text-ink-500">•</span>
                <span className="text-xs text-ink-500">Updated {result.lastUpdated}</span>
              </div>
              
              <p className="text-sm text-ink-600 mb-4 leading-relaxed">{result.snippet}</p>
              
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-ink-500">{result.source}</span>
                <button className="btn btn-ghost text-xs px-3 py-1">
                  View Full Document
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Access Resources */}
      <div className="card-minimal p-6">
        <h4 className="font-medium text-ink-900 mb-4">Quick Access</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-ink-700">Essential Standards</h5>
            <div className="space-y-2 text-sm">
              <a href="#" className="block text-accent-600 hover:text-accent-700">Core Humanitarian Standard</a>
              <a href="#" className="block text-accent-600 hover:text-accent-700">Sphere Handbook</a>
              <a href="#" className="block text-accent-600 hover:text-accent-700">Inter-Agency Standing Committee Guidelines</a>
            </div>
          </div>
          
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-ink-700">NRC Procedures</h5>
            <div className="space-y-2 text-sm">
              <a href="#" className="block text-accent-600 hover:text-accent-700">Emergency Response Manual</a>
              <a href="#" className="block text-accent-600 hover:text-accent-700">Safety & Security Protocols</a>
              <a href="#" className="block text-accent-600 hover:text-accent-700">Program Quality Standards</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Tab Component (Tertiary level only)
const AdminTab: React.FC<{
  stats: MockStats;
  viewLevel: ViewLevel;
  isMobileView: boolean;
}> = ({ stats, viewLevel, isMobileView }) => {
  const [systemHealth] = useState({
    crisisEngine: 'operational',
    dataFeeds: 'operational', 
    knowledgeBase: 'maintenance',
    alerts: 'operational',
    storage: 'warning'
  });

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'operational': return 'text-sage-600';
      case 'warning': return 'text-sand-600';
      case 'maintenance': return 'text-ocean-600';
      case 'error': return 'text-accent-600';
      default: return 'text-ink-600';
    }
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'operational': return '✅';
      case 'warning': return '⚠️';
      case 'maintenance': return '🔧';
      case 'error': return '❌';
      default: return '❓';
    }
  };

  return (
    <div className="space-y-6">
      {/* System Health Dashboard */}
      <div className="card-minimal p-6">
        <h3 className="text-lg font-display text-ink-900 mb-4">System Health</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(systemHealth).map(([system, status]) => (
            <div key={system} className="border border-ink-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-ink-900 capitalize">{system.replace(/([A-Z])/g, ' $1')}</h4>
                <span className="text-lg">{getHealthIcon(status)}</span>
              </div>
              <div className={cn("text-sm font-medium capitalize", getHealthColor(status))}>
                {status}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Data Quality Metrics */}
      <div className="card-minimal p-6">
        <h3 className="text-lg font-display text-ink-900 mb-4">Data Quality</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-600">Data Completeness</span>
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-ink-200">
                <div className="h-full bg-sage-500" style={{ width: '94%' }}></div>
              </div>
              <span className="text-sm font-medium text-sage-600">94%</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-600">Source Reliability</span>
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-ink-200">
                <div className="h-full bg-ocean-500" style={{ width: '87%' }}></div>
              </div>
              <span className="text-sm font-medium text-ocean-600">87%</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-600">Timeliness Score</span>
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-ink-200">
                <div className="h-full bg-sand-500" style={{ width: '76%' }}></div>
              </div>
              <span className="text-sm font-medium text-sand-600">76%</span>
            </div>
          </div>
        </div>
      </div>

      {/* User Activity */}
      <div className="card-minimal p-6">
        <h3 className="text-lg font-display text-ink-900 mb-4">User Activity</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-ink-900">24</div>
            <div className="text-xs font-mono uppercase tracking-wider text-ink-500">Active Users</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-ocean-600">156</div>
            <div className="text-xs font-mono uppercase tracking-wider text-ink-500">Decisions Today</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-sage-600">89%</div>
            <div className="text-xs font-mono uppercase tracking-wider text-ink-500">Uptime</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-accent-600">12</div>
            <div className="text-xs font-mono uppercase tracking-wider text-ink-500">Errors</div>
          </div>
        </div>
      </div>

      {/* System Configuration */}
      <div className="card-minimal p-6">
        <h3 className="text-lg font-display text-ink-900 mb-4">Configuration</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-600">Auto Refresh Interval</span>
            <select className="input-minimal py-1 text-sm w-32">
              <option>30 seconds</option>
              <option>1 minute</option>
              <option>5 minutes</option>
            </select>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-600">Alert Threshold</span>
            <select className="input-minimal py-1 text-sm w-32">
              <option>High</option>
              <option>Medium</option>
              <option>Low</option>
            </select>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-600">Data Retention</span>
            <select className="input-minimal py-1 text-sm w-32">
              <option>90 days</option>
              <option>180 days</option>
              <option>1 year</option>
            </select>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button className="btn btn-primary">Save Configuration</button>
          <button className="btn btn-ghost">Reset to Defaults</button>
        </div>
      </div>
    </div>
  );
};

export default SituationAnalysis;